<template>
  <v-hover>
    <template #default="{ isHovering, props }">
      <v-card
        ref="cardRef"
        v-bind="props"
        :to="{ name: 'Product', params: { id: product.productId } }"
        class="product-card d-flex flex-column"
        height="100%"
        :tabindex="tabindex"
        @keydown="emit('keydown', $event)"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
      >
        <v-img
          class="flex-grow-0"
          aspect-ratio="1"
          :lazy-src="emptyPlaceholderImage"
          :src="product.imageUrl ? fitImage(product.imageUrl, 520, 520) : placeholderImage"
          :srcset="product.imageUrl ? getSrcset(product.imageUrl, 520, 520) : placeholderImage"
          :alt="product.title"
        >
          <v-icon
            v-if="isProduct(componentProps.product) ? componentProps.product.hasEpeat : false"
            size="x-large"
            color="#4FB053"
            :icon="mdiLeafCircle"
            class="position-absolute bottom-0 right-0 ma-2"
          />
          <sf-condition-chip
            v-if="product.condition && product.condition !== Condition.NEW"
            :condition="product.condition"
            class="position-absolute bottom-0 left-0 ma-2"
          />
        </v-img>
        <div
          v-if="!$vuetify.display.smAndDown"
          v-show="isHovering"
          class="position-absolute top-0 right-0"
        >
          <div class="d-flex mt-1 mr-2">
            <comparison-button :product="product" />
            <cart-button
              v-if="!isRequest"
              is-icon
              :product-id="product.productId"
              :quantity="1"
              :supplier-row-id="supplierRowId"
              :on-click-event="addToCartEvent"
            />
          </div>
        </div>
        <v-card-text class="d-flex flex-column">
          <sf-text ref="title" :class="{ 'two-lines': small }" class="text-justify mb-1">
            {{ product.title }}
          </sf-text>
          <product-identifiers
            v-if="showProductIdentifiers"
            class="mb-1"
            :article-number="product.articleNumber"
            :product-id="product.productId"
          />
          <v-spacer />

          <topi-cart-rental-summary-label
            :loading="loadingRealtimePrice"
            :price="product.price"
            :product-id="product.productId"
          />
          <div class="d-flex flex-row align-center mt-1">
            <product-stock-dot :stock-status="product.stock?.status" class="mr-2" />
            <v-spacer />
            <div class="d-flex flex-column">
              <div
                v-if="
                  product.price && (product.price.subscriptionModel || product.price.packagingModel)
                "
                ref="price-addition"
                class="d-flex justify-end text-primary"
              >
                <billing-period
                  v-if="product.price.subscriptionModel"
                  class="text-body-1"
                  :subscription-model="product.price.subscriptionModel"
                />
                <packaging-info
                  v-if="product.price.packagingModel"
                  class="text-body-1"
                  :packaging-model="product.price.packagingModel"
                />
              </div>
              <product-price
                v-if="product.price"
                :price="product.price.resellPrice"
                :product-id="product.productId"
                :show-loading-indicator="showRealtimeLoadingIndicator"
                :loading-realtime-prices="loadingRealtimePrice"
              />
            </div>
          </div>
        </v-card-text>
      </v-card>
    </template>
  </v-hover>
</template>

<script setup lang="ts">
import BillingPeriod from '@/components/BillingPeriod.vue'
import PackagingInfo from '@/components/PackagingInfo.vue'
import CartButton from '@/components/button/CartButton.vue'
import SfConditionChip from '@/components/chips/SfConditionChip.vue'
import SfText from '@/components/text/SfText.vue'
import { Condition, MediumProduct, ShortProduct } from '@/generatedTypes'
import { emptyPlaceholderImage, fitImage, getSrcset, placeholderImage } from '@/helpers'
import ComparisonButton from '@/modules/product/components/ComparisonButton.vue'
import ProductIdentifiers from '@/modules/product/components/ProductIdentifiers.vue'
import ProductPrice from '@/modules/product/components/ProductPrice.vue'
import ProductStockDot from '@/modules/product/components/ProductStockDot.vue'
import { isProduct } from '@/modules/search/helpers'
import TopiCartRentalSummaryLabel from '@/modules/topi/components/TopiCartRentalSummaryLabel.vue'
import AddToCartButtonEvents from '@/modules/tracking/events/helper/addToCartButton'
import { mdiLeafCircle } from '@mdi/js'
import { VueInstance } from '@vueuse/core'
import { PropType, computed, ref, watch } from 'vue'

const componentProps = defineProps({
  addToCartEvent: {
    type: Object as PropType<AddToCartButtonEvents>,
    required: true
  },
  product: {
    type: Object as PropType<MediumProduct | ShortProduct>,
    required: true
  },
  showProductIdentifiers: {
    type: Boolean,
    default: false
  },
  loadingRealtimePrices: {
    type: Array as PropType<number[]>,
    default: () => []
  },
  small: {
    type: Boolean,
    default: false
  },
  showRealtimeLoadingIndicator: {
    type: Boolean,
    default: false
  },
  tabindex: {
    type: Number,
    default: -1
  },
  isFocused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([
  // focus and blur events need to be declared as vue cannot infer them from the template
  'focus',
  'blur',
  'keydown'
])

const cardRef = ref<VueInstance | null>(null)

const supplierRowId = computed(() => {
  return componentProps.product.price?.supplierRowId || undefined
})

const isRequest = computed(() => {
  return componentProps.product.price && componentProps.product.price.resellPrice.value > 0
    ? false
    : true
})

const loadingRealtimePrice = computed(() => {
  return componentProps.loadingRealtimePrices.includes(componentProps.product.productId)
})

watch(
  () => componentProps.isFocused,
  () => {
    if (componentProps.isFocused) {
      cardRef.value?.$el.focus()
    } else {
      cardRef.value?.$el.blur()
    }
  }
)
</script>
